import React, { useState, useRef } from 'react'
import { useSnackbar } from 'notistack'
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
// import { DatePicker } from '@mui/x-date-pickers/DatePicker'
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { misService } from '../../services/misService'

const MISBulkUpload: React.FC<{ onDone?: () => void }> = ({ onDone }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [progressPercent, setProgressPercent] = useState<number>(0)
  const [result, setResult] = useState<any | null>(null)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const handleDownloadTemplate = () => {
    const csv = `RiderName,riderPublicRiderId,clientName,clientRiderId,month,year,week,Hub,City,PhoneNumber,orders,grossPayout,tds,clientDeduction,sd,damageCost,damageCostDeducted,fleet,trafficFine,trafficFineDeducted,batterySwap,onHold,others,cod,evRent,evRentDeducted,Insurance,remarks\n` +
      `Rider Name,rid-123,Client A,cli-123,11,2024,W1,Hub A,City A,9876543210,50,10000,500,500,100,200,150,300,400,250,500,600,350,700,800,Remark text here\n`
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'mis_bulk_template.csv'
    a.click()
    window.URL.revokeObjectURL(url)
    enqueueSnackbar('Template downloaded', { variant: 'success' })
  }

  const downloadFailuresCSV = (failures: any[]) => {
    if (!failures || failures.length === 0) return
    const header = ['rowNumber','riderPublicRiderId','reason']
    const rows = failures.map(f => [f.rowNumber, f.riderPublicRiderId || '', (f.reason || '').replace(/\n/g, ' ')])
    const csv = [header.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'mis_bulk_failures.csv'
    a.click()
    window.URL.revokeObjectURL(url)
    enqueueSnackbar('Failures CSV downloaded', { variant: 'info' })
  }

  const validateAndSetFile = (selectedFile?: File | null) => {
    if (!selectedFile) return
    const validExt = ['.csv', '.xlsx', '.xls']
    const name = selectedFile.name
    const ext = name.slice((Math.max(0, name.lastIndexOf('.')) || Infinity))
    if (!validExt.includes(ext.toLowerCase())) {
      enqueueSnackbar('Invalid file. Please upload CSV or Excel', { variant: 'error' })
      return
    }
    if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
      enqueueSnackbar('File too large (max 10MB)', { variant: 'error' })
      return
    }
    setFile(selectedFile)
    enqueueSnackbar(`File selected: ${selectedFile.name}`, { variant: 'info' })
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) validateAndSetFile(e.target.files[0])
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false)
    const dt = e.dataTransfer
    if (dt && dt.files && dt.files.length > 0) validateAndSetFile(dt.files[0])
  }

  const handleUpload = async () => {
    if (!file) return enqueueSnackbar('Please select a file first', { variant: 'warning' })
    if (!fromDate || !toDate) return enqueueSnackbar('Please provide From and To dates', { variant: 'warning' })

    if (!confirm('Start bulk upload?')) return

    try {
      setLoading(true)
      setProgressPercent(10)
      const res = await misService.bulkUpload(file, fromDate, toDate)
      setProgressPercent(100)
      setResult(res)
      // Show dialogs / alerts based on result
      if (res?.summary?.failureCount > 0 || (res?.failures && res.failures.length > 0)) {
        setShowErrorDialog(true)
        enqueueSnackbar('Upload completed with failures', { variant: 'warning' })
      } else {
        setShowSuccessDialog(true)
        enqueueSnackbar('Upload successful', { variant: 'success' })
      }
      if (onDone) onDone()
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || 'Upload failed'
      enqueueSnackbar(msg, { variant: 'error' })
      setResult(null)
    } finally {
      setLoading(false)
      setProgressPercent(0)
    }
  }

  const renderFailuresTable = () => {
    if (!result?.failures || result.failures.length === 0) return null
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Row</TableCell>
            <TableCell>Rider</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result.failures.map((f: any, idx: number) => (
            <TableRow key={idx}>
              <TableCell>{f.rowNumber}</TableCell>
              <TableCell>{f.riderPublicRiderId}</TableCell>
              <TableCell style={{ whiteSpace: 'normal' }}>{f.reason}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Bulk MIS Upload</Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Upload multiple MIS rows using CSV or Excel. Provide a From and To date which will be applied to all uploaded rows.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card >
            <CardHeader title="Upload File" subheader="CSV or Excel format (Max 10MB)" />
            <Box mr={2} ml={2}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Note: Include the new week column in the file if available; the date range is still required for period dates.</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>The system will automatically calculate the final payout based on the provided values, so there's no need to include a final payout column in your file. Make sure that data's are accurate, Once you upload the file there will be no editing allowed for SD field So be awar</Typography>   
            </Box>
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
                  Download Template
                </Button>
                <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 1 }}>
                  Download the template CSV to see the required format
                </Typography>
              </Box>

              <Box
                onDragEnter={(e) => { e.preventDefault(); setIsDragging(true) }}
                onDragOver={(e) => e.preventDefault()}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false) }}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragging ? '#1976d2' : '#ccc',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': { borderColor: '#1976d2' },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
                <Typography variant="h6">Drag and drop your file here</Typography>
                <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>or click to select a file</Typography>
                <input
                  ref={fileRef}
                  type="file"
                  id="mis-file-input"
                  onChange={handleFileInputChange}
                  accept=".csv,.xlsx,.xls"
                  style={{ display: 'none' }}
                />
                <label htmlFor="mis-file-input">
                  <Button variant="contained" component="span">Select File</Button>
                </label>
              </Box>

              {file && (
                <Box sx={{ mt: 3, p: 2, borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>✓ {file.name}</Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>Size: {(file.size / 1024).toFixed(2)} KB</Typography>
                  </Box>
                  <Button size="small" startIcon={<CloseIcon />} onClick={() => setFile(null)}>Remove</Button>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label="From date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="To date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{ minWidth: 160 }}
                />
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" color="primary" onClick={handleUpload} disabled={!file || loading}>
                  {loading ? (<><CircularProgress size={18} sx={{ mr: 1 }} /> Uploading...</>) : 'Start Upload'}
                </Button>
              </Box>

              {loading && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption">Uploading... {progressPercent}%</Typography>
                </Box>
              )}

            </CardContent>
          </Card>

          {result && (
            <Box sx={{ mt: 3 }}>
              <Alert severity={result?.summary?.failureCount > 0 ? 'warning' : 'success'}>
                {result?.summary ? (
                  <>
                    <strong>{result.summary.successCount || 0}</strong> rows processed successfully. <strong>{result.summary.failureCount || 0}</strong> failures.
                  </>
                ) : 'Upload completed'}
              </Alert>

              {result?.successes && result.successes.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6">Successes</Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Rider</TableCell>
                          <TableCell>deductedPending</TableCell>
                          <TableCell>misId</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.successes.map((s: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>{s.rowNumber}</TableCell>
                            <TableCell>{s.riderPublicRiderId}</TableCell>
                            <TableCell>{s.deductedPending}</TableCell>
                            <TableCell>{s.misId}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              )}

              {result?.failures && result.failures.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6">Failures</Typography>
                  <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
                    {renderFailuresTable()}
                  </Paper>

                  <Box sx={{ mt: 2 }}>
                    <Button variant="outlined" onClick={() => downloadFailuresCSV(result.failures)}>Download Failures CSV</Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Instructions" />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>1. Download Template</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>Click the "Download Template" button to get a sample CSV file with the correct format.</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>2. Provide Dates</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>Provide a From and To date — these will be applied to all uploaded MIS rows.</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>3. Upload</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>Select your CSV/Excel file and click "Start Upload". We will process and show a summary with any failures.</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>4. About Calculation</Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>MIS team no need to calculate finalpayout value, were it will be take care by this software, auto calculation based give value. </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Requirements</Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li><Typography variant="caption" sx={{ color: '#666' }}>Rider Public ID must exist in system</Typography></li>
                    <li><Typography variant="caption" sx={{ color: '#666' }}>Date format: YYYY-MM-DD</Typography></li>
                    <li><Typography variant="caption" sx={{ color: '#666' }}>File size max: 10MB</Typography></li>
                    <li><Typography variant="caption" sx={{ color: '#666' }}>Accepted: CSV, XLSX, XLS</Typography></li>
                  </ul>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Error dialog */}
        <Dialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#ffebee' }}>
            <ErrorIcon sx={{ color: '#d32f2f' }} /> Upload Errors
          </DialogTitle>
          <DialogContent dividers>
            {result?.failures && result.failures.length > 0 ? (
              <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                {renderFailuresTable()}
              </Paper>
            ) : <Typography>No failures reported</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowErrorDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Success dialog */}
        <Dialog open={showSuccessDialog} onClose={() => setShowSuccessDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#4caf50' }} /> Upload Completed
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>{result?.summary?.successCount || 0} Rows Processed</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>{result?.summary?.message || 'Upload completed successfully'}</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSuccessDialog(false)} variant="contained">Done</Button>
          </DialogActions>
        </Dialog>

      </Grid>
    </Container>
  )
}

export default MISBulkUpload
